import { createElement } from "../utils/element-handlers.utils";

class FloatingButton {
    private htmlButtonElement: HTMLElement | null = null;
    private htmlIconElement: HTMLElement | null = null;
    private htmlLabelElement: HTMLElement | null = null;
    private labelText: string | null = null;
    private floatingButtonSpinnerElement: HTMLElement | null = null;
    private _isHidden: boolean = false;
    private _isBusy: boolean = false;
    private _isActive: boolean = false;

    constructor(
        initialButtonElementOrBoolean: HTMLElement | boolean,
        initialImageIconElementOrLabelTextOrBoolean: HTMLImageElement | string | boolean,
        initialConditionalIconURL: string,
        optionalClickActionFunction?: () => void
    ) {
        if (initialButtonElementOrBoolean) {
            if (initialButtonElementOrBoolean === true) {
                this.setButtonElement(
                    createElement('button', 'n-floating-button')
                );
            }
            else {
                this.setButtonElement(initialButtonElementOrBoolean);
            }
    
            if (typeof optionalClickActionFunction === 'function') {
                this.htmlButtonElement?.addEventListener('click', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    optionalClickActionFunction();
                });
            }
    
            if (initialImageIconElementOrLabelTextOrBoolean) {
                if (initialImageIconElementOrLabelTextOrBoolean === true) {
                    this.setIcon(true, initialConditionalIconURL);
                }
                else if (initialImageIconElementOrLabelTextOrBoolean instanceof HTMLElement) {
                    this.setIcon(initialImageIconElementOrLabelTextOrBoolean, initialConditionalIconURL);
                }
                else if (typeof initialImageIconElementOrLabelTextOrBoolean === 'string') {
                    this.setLabel(initialImageIconElementOrLabelTextOrBoolean);
                }
            }
        }
    }

    public setButtonElement(buttonElementOrBoolean: HTMLElement | true) {
        if (this.htmlButtonElement) {
            throw 'FloatingButton instance: Existing button element. Cannot assign other one.';
        }

        if (buttonElementOrBoolean === true) {
            this.htmlButtonElement = createElement('button', 'n-floating-button');
        }
        else {
            this.htmlButtonElement = buttonElementOrBoolean;
            this.htmlButtonElement.classList.add('n-floating-button');
        }

        if (this.htmlButtonElement.classList.contains('n-floating-button-hidden')) {
            this._isHidden = false;
            this.setHidden();
        }

        if (this.htmlButtonElement.classList.contains('n-floating-button-busy')) {
            this._isBusy = false;
            this.setBusy();
        }

        if (this.htmlButtonElement.classList.contains('n-floating-button-active')) {
            this._isActive = false;
            this.setActive();
        }
    }

    public getButtonElement() {
        return this.htmlButtonElement;
    }

    public setLabel(text: string) {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setLabel(String): Cannot set a label. First set a buttonElement.')
        }

        this.labelText = text || null;
        
        if (this.labelText) {
            this.htmlButtonElement.setAttribute('aria-label', this.labelText)
        }
        else {
            this.htmlButtonElement.removeAttribute('aria-label')
        }

        if (this.htmlIconElement) {
            return;
        }

        if (!this.htmlLabelElement) {
            this.htmlLabelElement = createElement('span', 'n-floating-button-label');
        }

        this.htmlLabelElement.innerHTML = this.labelText || '';
        this.htmlButtonElement.append(this.htmlLabelElement);
    }

    public unsetLabel(booleanToRemoveText: boolean = false) {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.unsetLabel(booleanToRemoveText: Boolean): Cannot unset a label. First set a buttonElement.')
        }

        if (this.htmlLabelElement) {
            this.htmlLabelElement.remove();
            this.htmlLabelElement = null;
        }

        if (booleanToRemoveText === true) {
            this.labelText = null;
        }
    }

    public setIcon(imageIconElementOrIconURLOrBoolean: HTMLElement | string | boolean, conditionalIconURL?: string) {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setIcon(): No buttonElement set. First set a buttonElement to use this function.')
        }

        if (this.htmlLabelElement) {
            this.unsetLabel();
        }

        let newHtmlIconElement: HTMLElement | null = null;

        if (typeof imageIconElementOrIconURLOrBoolean === 'string') {
            if (conditionalIconURL !== undefined) {
                throw new Error('FloatingButton.setIcon(String): First argument is an image URL. Please remove the second argument.');
            }

            if (this.htmlIconElement) {
                this.htmlIconElement.setAttribute('src', imageIconElementOrIconURLOrBoolean)
            }
            else {
                this.htmlIconElement = createElement('img', {
                    className: 'n-floating-button-icon',
                    src: imageIconElementOrIconURLOrBoolean
                });

                this.htmlButtonElement.append(this.htmlIconElement);
            }

            newHtmlIconElement = this.htmlIconElement;
        }
        else if (imageIconElementOrIconURLOrBoolean instanceof HTMLElement) {
            if (imageIconElementOrIconURLOrBoolean.tagName !== 'IMG') {
                throw new Error('FloatingButton.setIcon(HTMLImageElement): Argument imageIconElement have to be a IMG tag');
            }

            if (conditionalIconURL !== undefined) {
                throw new Error('FloatingButton.setIcon(HTMLElement): Cannot set icon URL to imageIconElement. Please remove the second argument.');
            }

            newHtmlIconElement = imageIconElementOrIconURLOrBoolean;
            newHtmlIconElement.classList.add('n-floating-button-icon');
        }
        else if (imageIconElementOrIconURLOrBoolean === true) {
            if (typeof conditionalIconURL === 'string') {
                newHtmlIconElement = createElement('img', {
                    className: 'n-floating-button-icon',
                    src: conditionalIconURL
                });
            }
            else if (conditionalIconURL === undefined) {
                throw new Error('FloatingButton.setIcon(Boolean, String): The second argument is a required String -> conditionalIconURL');
            }
            else {
                throw new Error('FloatingButton.setIcon(Boolean, String): Invalid argument -> conditionalIconURL must be a string');
            }
        }

        if (newHtmlIconElement !== this.htmlIconElement) {
            if (this.htmlIconElement) {
                this.htmlIconElement.remove();
            }

            this.htmlButtonElement.append(newHtmlIconElement as Node);
            this.htmlIconElement = newHtmlIconElement;
        }
    }

    public unsetIcon() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setIcon(): No buttonElement set. First set a buttonElement to use this function.')
        }

        if (this.htmlIconElement) {
            this.htmlIconElement.remove();
            this.htmlIconElement = null;
        }

        if (this.labelText) {
            this.setLabel(this.labelText);
        }
    }

    public setHidden() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setHidden(): First set a buttonElement.')
        }

        if (!this._isHidden) {
            this.htmlButtonElement.classList.add('n-floating-button-hidden');
            this._isHidden = true;
        }
    }

    public unsetHidden() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.unsetHidden(): First set a buttonElement.')
        }

        if (this._isHidden) {
            this.htmlButtonElement.classList.remove('n-floating-button-hidden');
            this._isHidden = false;
        }
    }

    public isHidden() {
        return this._isHidden;
    }

    public setBusy() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setBusy(): First set a buttonElement.')
        }

        if (!this._isBusy) {
            if (!this.floatingButtonSpinnerElement) {
                this.floatingButtonSpinnerElement = createElement('div', 'n-floating-button-spinner')
            }

            this.htmlButtonElement.classList.add('n-floating-button-busy');
            this.htmlButtonElement.append(this.floatingButtonSpinnerElement);
            this._isBusy = true;
        }
    }

    public unsetBusy() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.unsetBusy(): First set a buttonElement.')
        }

        if (this._isBusy) {
            this.htmlButtonElement.classList.remove('n-floating-button-busy');
            this.floatingButtonSpinnerElement?.remove();
            this._isBusy = false;
        }
    }

    public isBusy() {
        return this._isBusy;
    }

    public setActive() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.setActive(): First set a buttonElement.')
        }

        if (!this._isActive) {
            this.htmlButtonElement.classList.add('n-floating-button-active');
            this._isActive = true;
        }
    }

    public unsetActive() {
        if (!this.htmlButtonElement) {
            throw new Error('FloatingButton.unsetActive(): First set a buttonElement.')
        }

        if (this._isActive) {
            this.htmlButtonElement.classList.remove('n-floating-button-active');
            this._isActive = false;
        }
    }

    public isActive() {
        return this._isActive;
    }
}

export default FloatingButton;
